import { type ClipsListResponse, type PublicClip } from '@air/api';
import { type Board, type Clip, type ClipAndBoardListItem, type ListResponse, type PublicBoard } from '@air/api/types';
import { type Upload } from '@air/redux-uploader';

import { type SearchClipsAndBoardsResponse } from '~/types/KanbanData';

type UseMixedGalleryParams = {
  type: ClipAndBoardListItem['type'];
};

export type UseGalleryMixedAsset<C> = {
  type: 'asset' | 'file';
  item: C;
} & UseMixedGalleryParams;

export type UseGalleryMixedBoard<B> = {
  type: 'board';
  item: B;
} & UseMixedGalleryParams;

export type UseGalleryMixedItem = UseGalleryMixedBoard<Board | PublicBoard> | UseGalleryMixedAsset<Clip | PublicClip>;

export const itemIsBoard = (item: UseGalleryMixedItem): item is UseGalleryMixedBoard<Board> => item.type === 'board';

export const itemIsAsset = (item: UseGalleryMixedItem): item is UseGalleryMixedAsset<Clip> => item.type === 'asset';
export const itemIsPublicAsset = (item: UseGalleryMixedItem): item is UseGalleryMixedAsset<PublicClip> =>
  item.type === 'asset';

export const itemIsFile = (item: UseGalleryMixedItem): item is UseGalleryMixedAsset<Clip> => item.type === 'file';

export type UseGalleryMixedData<B, C> = {
  data: (UseGalleryMixedBoard<B> | UseGalleryMixedAsset<C>)[];
  isLoading: boolean;
  isLoadingMore: boolean;
  total: number;
  hasMore: boolean;
  loadNextPage: () => void;
  isEmpty: boolean;
};

export type UsePrivateGalleryMixedData = {
  uploads: Upload[];
} & UseGalleryMixedData<Board, Clip>;

export type UseGalleryDataInfo<T> = {
  items: T[];
  isLoading: boolean;
  total: number;
  hasMore: boolean;
};

export type UseGalleryViewItemsReturn<B, C> = {
  data: {
    boards: UseGalleryDataInfo<B>;
    clips: UseGalleryDataInfo<C>;
    files: UseGalleryDataInfo<C>;
  };
  isLoading: boolean;
  loadNextPage: () => void;
  isEmpty: boolean;
};

export type UseGalleryViewItemsInfoParams<T> = {
  data?: T;
  loadNextPage: () => void;
  isValidating: boolean;
  isVisible: boolean;
  hasMore: boolean;
};

export type UseGalleryViewItemsParams<B, C> = {
  boards: UseGalleryViewItemsInfoParams<ListResponse<B>[]>;
  clips: UseGalleryViewItemsInfoParams<ClipsListResponse<C>[]>;
  files: UseGalleryViewItemsInfoParams<ClipsListResponse<C>[]>;
};

export const defaultBoardsData = {
  data: [],
  total: 0,
  pagination: {
    cursor: null,
    hasMore: false,
  },
};

export const defaultAssetsData = {
  data: {
    clips: [],
    total: 0,
  },
  pagination: {
    cursor: null,
    hasMore: false,
  },
};

export const defaultTableViewData: SearchClipsAndBoardsResponse = {
  data: [],
  pagination: {
    cursor: null,
    hasMore: false,
  },
  total: 0,
};
